// MUI-free copy of Ui-components/home/HomeFAQSplit (homepage and mobile app page). The MUI Accordion is
// replaced by FAQRow (same markup roles, same expand/collapse); answers stay in the server HTML.
import {memo} from 'react';
import type {CSSProperties} from 'react';
import Container from '@/components/atoms/Container';
import AccordionItem from '@/components/atoms/AccordionItem';
import type {FAQCategory} from '@/components/plain-sections/FAQ';
import {cdnUrl} from '@/util/cdnImage';
import {useInView} from '@/hooks/useInView';
import {useOpenItem} from '@/hooks/useOpenItem';
import styles from './HomeFAQSplit.module.scss';

// Answers containing block elements (see the render below).

// The question-mark artwork is the left column's background (URL comes from the CDN env var).
const leftColStyle: CSSProperties = {
    backgroundImage: `url(${cdnUrl('home-page-updated-assets/faq-help-card-question-mark.webp')})`
};

interface HomeFAQSplitProps {
    heading: string;
    subheading?: string;
    faqData: FAQCategory[];
    /** Caps the card at 420px (centred) below md, like the MUI original; off keeps the homepage's full-width card. */
    capCardOnTablet?: boolean;
    /** Left-card artwork URL; defaults to the home page's question-mark card. */
    image?: string;
    /** The artwork's width / height, so the card shows it uncropped (e.g. `'872 / 899'`). */
    imageAspectRatio?: string;
    /** Smaller heading and a 14px subheading, so more of the artwork shows. */
    compactHeader?: boolean;
}

const cx = (...names: Array<string | false | undefined>) => names.filter(Boolean).join(' ');

// A custom card: its own artwork and aspect ratio (the home card's ratio stays in the stylesheet).
const customLeftColStyle = (image: string, aspectRatio: string | undefined, showImage: boolean): CSSProperties => ({
    ...(showImage && {backgroundImage: `url("${image}")`}),
    ...(aspectRatio && {aspectRatio})
});

const HomeFAQSplit = ({
    heading,
    subheading,
    faqData,
    capCardOnTablet = false,
    image,
    imageAspectRatio,
    compactHeader = false
}: HomeFAQSplitProps) => {
    const allQuestions = faqData?.flatMap((category) => category.questions) || [];
    const [expanded, handleToggle] = useOpenItem();
    // The card artwork is a CSS background, and those load as soon as they are styled — even far below the fold
    // (597 KB on the mobile app page). Set it once the card is near the viewport.
    const [cardRef, cardNear] = useInView({triggerOnce: true, rootMargin: '400px'});

    return (
        <div>
            <Container>
                <div className={styles.grid}>
                    <div className={cx(styles.item, styles.itemLeft)}>
                        <div
                            ref={cardRef}
                            className={cx(styles.leftCol, capCardOnTablet && styles.leftColCapped)}
                            style={image ? customLeftColStyle(image, imageAspectRatio, cardNear) : cardNear ? leftColStyle : undefined}
                        >
                            <h2 className={cx('font-headers', styles.heading, compactHeader && styles.headingCompact)}>{heading}</h2>
                            {subheading && (
                                <p className={cx('font-regular-text', styles.subheading, compactHeader && styles.subheadingCompact)}>
                                    {subheading}
                                </p>
                            )}
                        </div>
                    </div>
                    <div className={cx(styles.item, styles.itemRight)}>
                        <div className={styles.questionsList}>
                            {allQuestions.map((item, idx) => (
                                <AccordionItem
                                    key={idx}
                                    variant="soft"
                                    question={item.q}
                                    answer={item.a}
                                    expanded={expanded === idx}
                                    onToggle={() => handleToggle(idx)}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </Container>
        </div>
    );
};

export default memo(HomeFAQSplit);
